import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Send httpOnly auth cookies with every request (JWT is no longer stored in localStorage)
axios.defaults.withCredentials = true;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const axiosInstance = axios.create({
        baseURL: API,
        withCredentials: true
    });

    const fetchUser = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/me`);
            setUser(response.data);
        } catch (error) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password, rememberMe = false) => {
        const response = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
        const { user: userData } = response.data;
        setUser(userData);
        return userData;
    };

    const register = async (email, password, name, accountType = 'individual', companyName = null, workEmail = null, country = null, phone = null) => {
        const payload = { email, password, name, account_type: accountType };

        if (accountType === 'laboratory') {
            payload.company_name = companyName;
            payload.work_email = workEmail;
            payload.country = country;
            payload.phone = phone;
        }

        const response = await axios.post(`${API}/auth/register`, payload);
        const { user: userData } = response.data;
        setUser(userData);
        return userData;
    };

    const loginWithGoogle = async (sessionId, accountType = 'individual', companyName = null, country = null) => {
        const response = await axios.post(`${API}/auth/google/session`, {
            session_id: sessionId,
            account_type: accountType,
            company_name: companyName,
            country: country
        });
        const { user: userData } = response.data;
        setUser(userData);
        return userData;
    };

    const initiateGoogleLogin = (accountType = 'individual', companyName = null, country = null) => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/auth/callback';

        if (accountType || companyName || country) {
            sessionStorage.setItem('oauth_account_type', accountType);
            if (companyName) sessionStorage.setItem('oauth_company_name', companyName);
            if (country) sessionStorage.setItem('oauth_country', country);
        }

        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {});
        } catch (error) {
            console.error('Logout error:', error);
        }
        setUser(null);
    };

    const setUserDirectly = (userData) => {
        setUser(userData);
    };

    const value = {
        user,
        loading,
        login,
        register,
        loginWithGoogle,
        initiateGoogleLogin,
        logout,
        setUserDirectly,
        isAuthenticated: !!user,
        api: axiosInstance
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
